import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Sparkles,
  Zap,
  Palette,
  Layers,
  Trash2,
  Loader2,
  Monitor,
  Smartphone,
  Check,
  SendHorizontal,
  Globe2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useSession } from '@/lib/session'
import { useI18n, translateError, type Translate } from '@/i18n'
import { Seo } from '@/components/Seo'
import {
  Section,
  SectionHeading,
  FeatureGrid,
  Feature,
  CtaBand,
} from '@/components/Page'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { SpaceGlobe } from '@/components/SpaceGlobe'
import { cn } from '@/lib/utils'

export default function Home() {
  const { t } = useI18n()
  const { user } = useSession()
  const health = useQuery({ queryKey: ['health'], queryFn: api.health })
  const connected = health.data?.database === 'configured'

  // Interactive Sandbox state
  const [prompt, setPrompt] = useState('')
  const [selectedStyle, setSelectedStyle] = useState<'tech' | 'luxury' | 'creative'>('tech')
  const [activeDevice, setActiveDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [accentColor, setAccentColor] = useState<'cyan' | 'amber' | 'emerald' | 'violet'>('cyan')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedSuccess, setGeneratedSuccess] = useState(false)

  const handleApplyPrompt = (text: string) => {
    setPrompt(text)
    setIsGenerating(true)
    setGeneratedSuccess(false)
    setTimeout(() => {
      setIsGenerating(false)
      setGeneratedSuccess(true)
      setTimeout(() => setGeneratedSuccess(false), 3000)
    }, 700)
  }

  return (
    <>
      <Seo />

      {/* ── 1. Space-Age Hero Section with 3D Globe & Satellites ───────────── */}
      <Section tone="ink" rhythm="hero" texture="grid" className="relative overflow-hidden pt-8 sm:pt-14 pb-16 sm:pb-24">
        {/* Floating radiance orbs */}
        <div data-upbo-src="src/pages/Home.tsx:67"
          aria-hidden
          className="glow animate-pulse-glow pointer-events-none absolute -top-40 right-0 size-[36rem] sm:size-[48rem] opacity-75 blur-3xl bg-gradient-to-br from-primary/30 to-sky-600/20"
        />
        <div data-upbo-src="src/pages/Home.tsx:71"
          aria-hidden
          className="glow animate-float pointer-events-none absolute -bottom-32 -left-20 size-[30rem] sm:size-[40rem] opacity-50 blur-3xl bg-gradient-to-tr from-cyan-500/20 to-amber-500/15"
        />
        <div data-upbo-src="src/pages/Home.tsx:75" aria-hidden className="tex-noise pointer-events-none absolute inset-0 opacity-30" />

        <div data-upbo-src="src/pages/Home.tsx:77" className="relative z-10">
          <div data-upbo-src="src/pages/Home.tsx:78" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Col: Space-Age Headline & Value Proposition */}
            <div data-upbo-src="src/pages/Home.tsx:80" className="lg:col-span-7 flex flex-col items-start">
              {/* Headline */}
              <h1 data-upbo-src="src/pages/Home.tsx:82" className="max-w-3xl text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[1.12] sm:leading-[1.06] tracking-[-0.035em] reveal reveal-delay-1">
                <span data-upbo-src="src/pages/Home.tsx:83" className="text-shimmer">{t('home.hero.title')}</span>
              </h1>

              <p data-upbo-src="src/pages/Home.tsx:86" className="mt-4 sm:mt-6 max-w-xl text-sm sm:text-lg leading-relaxed text-brand-ink-foreground/80 reveal reveal-delay-2">
                {t('home.hero.subtitle')}
              </p>

              {/* Action Buttons - Touch-friendly */}
              <div data-upbo-src="src/pages/Home.tsx:91" className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto reveal reveal-delay-3">
                {user ? (
                  <Button asChild size="lg" className="h-12 bg-brand-ink-foreground text-brand-ink hover:bg-brand-ink-foreground/90 font-medium shadow-lg shadow-black/20">
                    <Link to="/account">
                      <span data-upbo-src="src/pages/Home.tsx:95">{user.name || user.email}</span>
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild size="lg" className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-lg shadow-primary/30 transition-all hover:scale-[1.02]">
                      <Link to="/register">
                        <Zap className="size-4 mr-1.5 fill-current" />
                        {t('home.hero.startBuilding')}
                      </Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="h-12 border-border/80 bg-background/50 backdrop-blur-sm hover:bg-background/80">
                      <a data-upbo-src="src/pages/Home.tsx:108" href="#showcase">
                        {t('home.hero.exploreShowcase')}
                        <ArrowRight className="size-4 ml-1.5" />
                      </a>
                    </Button>
                  </>
                )}
              </div>

              {/* ── Interactive Web Creation Sandbox Playground ──────────────── */}
              <div data-upbo-src="src/pages/Home.tsx:118" className="mt-8 sm:mt-10 w-full reveal reveal-delay-3">
                <div data-upbo-src="src/pages/Home.tsx:119" className="relative rounded-2xl border border-sky-500/25 bg-card/70 p-4 sm:p-6 shadow-2xl backdrop-blur-xl">
                  {/* Top toolbar */}
                  <div data-upbo-src="src/pages/Home.tsx:121" className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
                    <div data-upbo-src="src/pages/Home.tsx:122" className="flex items-center gap-2">
                      <span data-upbo-src="src/pages/Home.tsx:123" className="size-2.5 rounded-full bg-red-500/80" />
                      <span data-upbo-src="src/pages/Home.tsx:124" className="size-2.5 rounded-full bg-amber-500/80" />
                      <span data-upbo-src="src/pages/Home.tsx:125" className="size-2.5 rounded-full bg-emerald-500/80" />
                      <span data-upbo-src="src/pages/Home.tsx:126" className="ml-2 font-mono text-[11px] sm:text-xs text-cyan-400 font-medium">{t('home.hero.readyBadge')}</span>
                    </div>

                    {/* Accent selector & Device switcher */}
                    <div data-upbo-src="src/pages/Home.tsx:130" className="flex items-center gap-2">
                      <div data-upbo-src="src/pages/Home.tsx:131" className="flex items-center gap-1.5 bg-background/70 px-2 py-1 rounded-lg border border-border/60">
                        {(['cyan', 'amber', 'emerald', 'violet'] as const).map((color) => (
                          <button data-upbo-src="src/pages/Home.tsx:133"
                            key={color}
                            onClick={() => setAccentColor(color)}
                            aria-label={`Accent ${color}`}
                            className={cn(
                              'size-4 rounded-full transition-transform',
                              color === 'cyan' && 'bg-cyan-400',
                              color === 'amber' && 'bg-amber-400',
                              color === 'emerald' && 'bg-emerald-400',
                              color === 'violet' && 'bg-purple-400',
                              accentColor === color ? 'scale-125 ring-2 ring-primary ring-offset-1 ring-offset-background' : 'opacity-70 hover:opacity-100'
                            )}
                          />
                        ))}
                      </div>

                      <div data-upbo-src="src/pages/Home.tsx:149" className="flex items-center bg-background/70 p-0.5 rounded-lg border border-border/60">
                        <button data-upbo-src="src/pages/Home.tsx:150"
                          onClick={() => setActiveDevice('desktop')}
                          className={cn('p-1 rounded-md transition-all', activeDevice === 'desktop' ? 'bg-primary/20 text-primary' : 'text-muted-foreground')}
                          title="Desktop view"
                        >
                          <Monitor className="size-3.5" />
                        </button>
                        <button data-upbo-src="src/pages/Home.tsx:157"
                          onClick={() => setActiveDevice('mobile')}
                          className={cn('p-1 rounded-md transition-all', activeDevice === 'mobile' ? 'bg-primary/20 text-primary' : 'text-muted-foreground')}
                          title="Mobile view"
                        >
                          <Smartphone className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Input Prompt bar */}
                  <div data-upbo-src="src/pages/Home.tsx:169" className="mt-3.5 flex flex-col sm:flex-row gap-2">
                    <div data-upbo-src="src/pages/Home.tsx:170" className="relative flex-1">
                      <Input
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={t('home.hero.inputPlaceholder')}
                        className="h-11 sm:h-12 bg-background/90 text-sm pl-3.5 pr-8 border-border/80 focus:border-primary"
                      />
                      {prompt && (
                        <button data-upbo-src="src/pages/Home.tsx:178"
                          onClick={() => setPrompt('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <Button
                      onClick={() => handleApplyPrompt(prompt || t('home.hero.promptSuggestion1'))}
                      disabled={isGenerating}
                      className="h-11 sm:h-12 px-5 font-medium shadow-md shadow-primary/20 shrink-0"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="size-4 mr-1.5 animate-spin" />
                          {t('home.hero.generating')}
                        </>
                      ) : generatedSuccess ? (
                        <>
                          <Check className="size-4 mr-1.5 text-emerald-400" />
                          Ready!
                        </>
                      ) : (
                        <>
                          <SendHorizontal className="size-4 mr-1.5" />
                          {t('home.hero.createSite')}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Quick Suggestion Pills - Horizontal Swipeable on Mobile */}
                  <div data-upbo-src="src/pages/Home.tsx:211" className="mt-3 flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
                    <span data-upbo-src="src/pages/Home.tsx:212" className="text-muted-foreground flex items-center gap-1 shrink-0">
                      <Sparkles className="size-3 text-cyan-400" />
                      Ideas:
                    </span>
                    {[
                      t('home.hero.promptSuggestion1'),
                      t('home.hero.promptSuggestion2'),
                      t('home.hero.promptSuggestion3'),
                    ].map((sug) => (
                      <button data-upbo-src="src/pages/Home.tsx:221"
                        key={sug}
                        onClick={() => handleApplyPrompt(sug)}
                        className="shrink-0 rounded-full border border-sky-500/25 bg-background/60 px-2.5 py-1 text-muted-foreground transition-colors hover:border-cyan-400 hover:text-foreground whitespace-nowrap"
                      >
                        {sug}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Col: The Interactive 3D Space Globe */}
            <div data-upbo-src="src/pages/Home.tsx:235" className="lg:col-span-5 reveal reveal-delay-2 w-full mt-4 lg:mt-0">
              <SpaceGlobe />
            </div>
          </div>

          {/* ── Metrics Grid (2x2 on Mobile, 4 Cols on Desktop) ─────────────── */}
          <div data-upbo-src="src/pages/Home.tsx:241" className="mt-12 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 reveal reveal-delay-3">
            {[
              { label: t('home.hero.metricSpeed'), desc: 'Sub-second generation', icon: Zap },
              { label: t('home.hero.metricFps'), desc: 'Buttery smooth motion', icon: Sparkles },
              { label: t('home.hero.metricStudio'), desc: 'Deep Space luxury', icon: Palette },
              { label: t('home.hero.metricMobile'), desc: 'Touch & swipe ready', icon: Layers },
            ].map((metric) => (
              <div data-upbo-src="src/pages/Home.tsx:248"
                key={metric.label}
                className="group relative rounded-xl border border-sky-500/20 bg-card/40 p-3 sm:p-4 backdrop-blur-md transition-all hover:border-cyan-400/50 hover:bg-card/70"
              >
                <metric.icon className="size-4 text-cyan-400 mb-1.5 sm:mb-2 transition-transform group-hover:scale-110" />
                <div data-upbo-src="src/pages/Home.tsx:253" className="font-semibold text-sm sm:text-base text-foreground">{metric.label}</div>
                <div data-upbo-src="src/pages/Home.tsx:254" className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">{metric.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 2. The 4 Pillars of Excellence ─────────────────────────────────── */}
      <Section rhythm="default">
        <SectionHeading
          eyebrow="Architectural Excellence"
          title={t('home.pillars.title')}
          body={t('home.pillars.body')}
        />

        <FeatureGrid cols={2} className="mt-8 sm:mt-12">
          <Feature
            icon={Zap}
            title={t('home.pillars.speed.title')}
            body={t('home.pillars.speed.body')}
          />
          <Feature
            icon={Globe2}
            title={t('home.pillars.motion.title')}
            body={t('home.pillars.motion.body')}
          />
          <Feature
            icon={Palette}
            title={t('home.pillars.design.title')}
            body={t('home.pillars.design.body')}
          />
          <Feature
            icon={Layers}
            title={t('home.pillars.responsive.title')}
            body={t('home.pillars.responsive.body')}
          />
        </FeatureGrid>
      </Section>

      {/* ── 3. Interactive Showcase Section ────────────────────────────────── */}
      <Section id="showcase" tone="tinted" rhythm="default">
        <SectionHeading
          eyebrow="Interactive Showcase"
          title={t('home.showcase.title')}
          body={t('home.showcase.subtitle')}
        />

        <div data-upbo-src="src/pages/Home.tsx:301" className="mt-8 flex flex-col items-center">
          {/* Style Tabs - Scrollable on mobile */}
          <div data-upbo-src="src/pages/Home.tsx:303" className="w-full sm:w-auto overflow-x-auto pb-1 no-scrollbar flex items-center justify-start sm:justify-center">
            <div data-upbo-src="src/pages/Home.tsx:304" className="inline-flex rounded-xl border border-sky-500/20 bg-card/60 p-1 backdrop-blur-md shrink-0">
              {(['tech', 'luxury', 'creative'] as const).map((style) => (
                <button data-upbo-src="src/pages/Home.tsx:306"
                  key={style}
                  onClick={() => setSelectedStyle(style)}
                  className={cn(
                    'px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap',
                    selectedStyle === style
                      ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {style === 'tech' && t('home.showcase.techMinimal')}
                  {style === 'luxury' && t('home.showcase.luxuryEditorial')}
                  {style === 'creative' && t('home.showcase.creativeDynamic')}
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Preview Card Container */}
          <div data-upbo-src="src/pages/Home.tsx:325" className="mt-6 sm:mt-8 w-full max-w-4xl">
            <div data-upbo-src="src/pages/Home.tsx:326" className="rounded-2xl sm:rounded-3xl border border-sky-500/25 bg-card/80 p-5 sm:p-10 shadow-2xl backdrop-blur-xl">
              <div data-upbo-src="src/pages/Home.tsx:327" className="flex flex-col md:flex-row gap-6 sm:gap-8 items-center">
                <div data-upbo-src="src/pages/Home.tsx:328" className="flex-1 text-left">
                  <div data-upbo-src="src/pages/Home.tsx:329" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono bg-cyan-500/15 text-cyan-400 mb-3 border border-cyan-500/30">
                    <Sparkles className="size-3" />
                    <span data-upbo-src="src/pages/Home.tsx:331">PREVIEW DIRECTION</span>
                  </div>
                  <h3 data-upbo-src="src/pages/Home.tsx:333" className="text-xl sm:text-2xl font-bold tracking-tight">
                    {selectedStyle === 'tech' && t('home.showcase.techMinimal')}
                    {selectedStyle === 'luxury' && t('home.showcase.luxuryEditorial')}
                    {selectedStyle === 'creative' && t('home.showcase.creativeDynamic')}
                  </h3>
                  <p data-upbo-src="src/pages/Home.tsx:338" className="mt-2.5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {selectedStyle === 'tech' && t('home.showcase.techDesc')}
                    {selectedStyle === 'luxury' && t('home.showcase.luxuryDesc')}
                    {selectedStyle === 'creative' && t('home.showcase.creativeDesc')}
                  </p>

                  <div data-upbo-src="src/pages/Home.tsx:344" className="mt-6 flex flex-wrap gap-2">
                    <span data-upbo-src="src/pages/Home.tsx:345" className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border">
                      60 FPS Fluid Motion
                    </span>
                    <span data-upbo-src="src/pages/Home.tsx:348" className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border">
                      Deep Space Glass
                    </span>
                    <span data-upbo-src="src/pages/Home.tsx:351" className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border">
                      Touch Gestures Ready
                    </span>
                  </div>
                </div>

                {/* Mockup Preview Box */}
                <div data-upbo-src="src/pages/Home.tsx:358" className="w-full md:w-80 h-52 sm:h-60 rounded-xl border border-sky-500/30 bg-gradient-to-br from-background via-card to-muted p-4 flex flex-col justify-between shadow-inner relative overflow-hidden">
                  <div data-upbo-src="src/pages/Home.tsx:359" className="flex items-center justify-between border-b border-border/50 pb-2">
                    <div data-upbo-src="src/pages/Home.tsx:360" className="flex gap-1.5">
                      <span data-upbo-src="src/pages/Home.tsx:361" className="size-2 rounded-full bg-red-400" />
                      <span data-upbo-src="src/pages/Home.tsx:362" className="size-2 rounded-full bg-amber-400" />
                      <span data-upbo-src="src/pages/Home.tsx:363" className="size-2 rounded-full bg-emerald-400" />
                    </div>
                    <span data-upbo-src="src/pages/Home.tsx:365" className="text-[10px] font-mono text-muted-foreground">upbo.preview</span>
                  </div>

                  <div data-upbo-src="src/pages/Home.tsx:368" className="my-auto space-y-2">
                    <div data-upbo-src="src/pages/Home.tsx:369" className="h-4 w-3/4 bg-primary/20 rounded-md animate-pulse" />
                    <div data-upbo-src="src/pages/Home.tsx:370" className="h-3 w-1/2 bg-muted-foreground/20 rounded-md" />
                    <div data-upbo-src="src/pages/Home.tsx:371" className="h-8 w-full bg-background/60 rounded-md border border-border flex items-center px-3 text-xs text-muted-foreground">
                      <span data-upbo-src="src/pages/Home.tsx:372" className="size-1.5 rounded-full bg-cyan-400 mr-2" />
                      Interactive Live Render
                    </div>
                  </div>

                  <div data-upbo-src="src/pages/Home.tsx:377" className="flex justify-end">
                    <span data-upbo-src="src/pages/Home.tsx:378" className="text-[11px] font-mono text-cyan-400 flex items-center gap-1">
                      Render Time: 12ms
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── 4. Live Workspace Projects (CRUD Demo) ─────────────────────────── */}
      <Section rhythm="default">
        <SectionHeading
          eyebrow="Interactive Workspace"
          title={t('home.crud.title')}
          body={t('home.crud.subtitle')}
        />

        <div data-upbo-src="src/pages/Home.tsx:397" className="mt-8 max-w-xl mx-auto">
          <ProjectsDemo t={t} />
        </div>
      </Section>

      {/* ── 5. Bottom Call to Action Band ─────────────────────────────────── */}
      <CtaBand
        title={t('home.cta.title')}
        body={t('home.cta.body')}
        primary={{
          label: t('home.cta.action'),
          to: user ? '/account' : '/register',
        }}
        secondary={{
          label: t('home.cta.contact'),
          to: '#showcase',
        }}
      />
    </>
  )
}

function ProjectsDemo({ t }: { t: Translate }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['posts'],
    queryFn: api.posts.list,
  })

  const addMutation = useMutation({
    mutationFn: (newTitle: string) => api.posts.create({ title: newTitle, body: '' }),
    onSuccess: () => {
      setTitle('')
      setError(null)
      qc.invalidateQueries({ queryKey: ['posts'] })
    },
    onError: (err) => setError(translateError(err, t, t('common.error'))),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.posts.remove(id),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['posts'] })
    },
    onError: (err) => setError(translateError(err, t, t('common.error'))),
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed) return
    addMutation.mutate(trimmed)
  }

  return (
    <div data-upbo-src="src/pages/Home.tsx:456" className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-xl">
      <form data-upbo-src="src/pages/Home.tsx:457" onSubmit={handleSubmit} className="flex gap-2.5">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('home.crud.placeholder')}
          disabled={addMutation.isPending}
          className="h-11 bg-background text-sm"
        />
        <Button
          type="submit"
          disabled={addMutation.isPending || !title.trim()}
          className="h-11 shrink-0 px-4 font-medium"
        >
          {addMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : t('home.crud.add')}
        </Button>
      </form>

      {error && <p data-upbo-src="src/pages/Home.tsx:474" className="mt-2 text-xs text-destructive">{error}</p>}

      <div data-upbo-src="src/pages/Home.tsx:476" className="mt-5 space-y-2 max-h-72 overflow-y-auto">
        {list.isLoading && (
          <div data-upbo-src="src/pages/Home.tsx:478" className="space-y-2">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        )}

        {list.data && list.data.length === 0 && (
          <p data-upbo-src="src/pages/Home.tsx:485" className="text-center text-xs text-muted-foreground py-6">{t('home.crud.empty')}</p>
        )}

        {list.data?.map((p) => (
          <div data-upbo-src="src/pages/Home.tsx:489"
            key={p.id}
            className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/70 bg-background/50 hover:bg-background transition-colors"
          >
            <div data-upbo-src="src/pages/Home.tsx:493" className="min-w-0">
              <p data-upbo-src="src/pages/Home.tsx:494" className="text-sm font-medium truncate">{p.title}</p>
              <p data-upbo-src="src/pages/Home.tsx:495" className="text-[10px] font-mono text-muted-foreground">
                {new Date(p.createdAt).toLocaleDateString()}
              </p>
            </div>
            {p.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeMutation.mutate(p.id)}
                disabled={removeMutation.isPending}
                className="text-muted-foreground hover:text-destructive shrink-0 size-8 p-0"
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
